import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ number, title, children }) => (
  <section className="space-y-3">
    <h2 className="text-lg font-bold text-white tracking-tight">
      {number}. {title}
    </h2>
    <div className="space-y-3 text-[15px] text-white/50 leading-relaxed">{children}</div>
  </section>
);

const Terms = () => {
  const effectiveDate = 'June 1, 2025';

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
          <Link to="/" className="text-white/30 hover:text-white transition-colors text-sm font-medium tracking-wide">
            ← Back
          </Link>
          <span className="text-xs text-white/20 tracking-widest uppercase font-medium">Wingcue</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-16 space-y-12">
        {/* Title block */}
        <div className="space-y-3">
          <h1 className="text-4xl font-black text-white tracking-tighter">Terms of Service</h1>
          <p className="text-sm text-white/25 font-medium">Effective Date: {effectiveDate}</p>
        </div>

        <p className="text-[15px] text-white/50 leading-relaxed border-l-2 border-[#E94E34]/40 pl-5">
          Please read these Terms of Service carefully before downloading or using Wingcue. By accessing or using the app, you agree to be bound by these terms.
        </p>

        <div className="space-y-10">
          <Section number="1" title="Acceptance of Terms">
            <p>
              By downloading, installing, or using the Wingcue mobile application ("App"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the App. These Terms constitute a legally binding agreement between you and Wingcue ("we," "us," or "our").
            </p>
          </Section>

          <Section number="2" title="Eligibility">
            <p>You must be at least 17 years of age to use Wingcue. By using the App, you represent and warrant that you are 17 years of age or older and have the legal capacity to enter into these Terms.</p>
          </Section>

          <Section number="3" title="Description of Service">
            <p>
              Wingcue is an AI-powered dating assistant application that provides conversation suggestions, screenshot analysis, and reply recommendations for use on third-party dating and messaging platforms. The App is designed to help users communicate more effectively.
            </p>
            <p>
              Wingcue is provided "as is" and "as available." We make no guarantees, representations, or warranties regarding the accuracy of AI-generated suggestions or the outcomes of conversations conducted using our suggestions. Results may vary.
            </p>
          </Section>

          <Section number="4" title="User Accounts">
            <p>You may be required to create an account to access certain features. You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You agree to notify us immediately of any unauthorized use of your account.</p>
            <p>You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.</p>
          </Section>

          <Section number="5" title="Acceptable Use">
            <p>You agree to use the App only for lawful purposes and in accordance with these Terms. You agree not to:</p>
            <ul className="list-disc list-inside space-y-1.5 pl-2">
              <li>Use the App for any fraudulent, deceptive, harassing, or illegal purpose.</li>
              <li>Use AI-generated content to deceive, manipulate, or harm other individuals.</li>
              <li>Upload screenshots or content that contains personal information of others without their consent.</li>
              <li>Reverse-engineer, decompile, or attempt to extract the source code of the App.</li>
              <li>Use the App in any manner that could disable, overburden, or impair our servers or networks.</li>
              <li>Attempt to gain unauthorized access to any part of the App or its related systems.</li>
              <li>Resell, sublicense, or commercially exploit the App or its outputs without our written permission.</li>
            </ul>
          </Section>

          <Section number="6" title="User Content & Screenshots">
            <p>When you upload screenshots or other content ("User Content") to Wingcue for analysis, you grant us a limited, non-exclusive, royalty-free license to process that content solely for the purpose of providing the requested AI analysis and suggestions to you.</p>
            <p>You represent and warrant that you have the right to share any content you submit and that doing so does not violate the privacy rights or any other rights of third parties.</p>
            <p>We do not use your User Content to train our AI models or share it with third parties except as described in our Privacy Policy.</p>
          </Section>

          <Section number="7" title="Intellectual Property">
            <p>The App, including its design, features, AI models, and all content created by Wingcue, is owned by Wingcue and protected by applicable copyright, trademark, and other intellectual property laws. You are granted a limited, non-exclusive, non-transferable, revocable license to use the App for personal, non-commercial purposes.</p>
          </Section>

          <Section number="8" title="In-App Purchases & Subscriptions">
            <p>Wingcue may offer premium features through in-app purchases or subscription plans processed via Apple App Store. All purchases are subject to Apple's payment terms. Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period.</p>
            <p>You may manage or cancel your subscription at any time through your Apple ID Account Settings. No refunds are provided for the unused portion of a subscription period, except as required by applicable law or Apple's refund policies.</p>
          </Section>

          <Section number="9" title="Disclaimers">
            <p>WINGCUE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>
            <p>We do not guarantee that the App will be uninterrupted, secure, or error-free, or that any defects will be corrected. AI-generated suggestions are for informational and entertainment purposes only.</p>
          </Section>

          <Section number="10" title="Limitation of Liability">
            <p>TO THE FULLEST EXTENT PERMITTED BY LAW, WINGCUE AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE APP, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.</p>
            <p>OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM THESE TERMS OR YOUR USE OF THE APP SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</p>
          </Section>

          <Section number="11" title="Third-Party Services">
            <p>The App may integrate with or link to third-party services, including dating platforms and messaging apps. We are not responsible for the content, privacy practices, or terms of any third-party service. Your use of third-party services is at your own risk and subject to their terms.</p>
          </Section>

          <Section number="12" title="Termination">
            <p>We reserve the right to suspend or terminate your access to the App at any time, with or without notice, for any reason, including if we reasonably believe you have violated these Terms. Upon termination, your right to use the App will immediately cease.</p>
          </Section>

          <Section number="13" title="Governing Law">
            <p>These Terms shall be governed by and construed in accordance with the laws of the State of California, United States, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in California.</p>
          </Section>

          <Section number="14" title="Changes to Terms">
            <p>We reserve the right to modify these Terms at any time. We will notify you of material changes by updating the "Effective Date" at the top of this page or through in-app notification. Your continued use of the App after any changes constitutes your acceptance of the revised Terms.</p>
          </Section>

          <Section number="15" title="Contact Us">
            <p>If you have any questions about these Terms, please contact us at:</p>
            <p className="text-white/60 font-medium">support@wingcue.com</p>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default Terms;